import type { CodexLimit, CodexUsage } from '@mac-remote/protocol';

/** One limit ready for display: what it is called, and how much of it is spent. */
export interface ShownLimit {
  /** Stable across polls, so React keeps the same node and the bar animates. */
  key: string;
  /** Header-sized: "5ч", "7д", or a model name. */
  label: string;
  /** Full name for the card and the tooltip. */
  title: string;
  /** 0..100, spent. Reported the way Codex reports it and the way the app shows
   *  it — one number, no inversion anywhere along the way to argue with. */
  used: number;
  resetsAt: number | null;
}

const clamp = (limit: CodexLimit): number => Math.max(0, Math.min(100, limit.percent));

/**
 * Colour by how full it is. Three quarters spent is worth noticing; nine tenths is
 * worth planning around.
 */
export function usedColor(used: number): 'jade' | 'amber' | 'red' {
  if (used >= 90) return 'red';
  if (used >= 75) return 'amber';
  return 'jade';
}

/** Window durations come from Codex, rather than assuming a particular plan. */
export function shownLimits(usage: Extract<CodexUsage, { ok: true }>): ShownLimit[] {
  return usage.windows.map((window) => ({
    key: window.key,
    label: window.windowMinutes === null ? 'Codex'
      : window.windowMinutes < 60 ? `${window.windowMinutes}м`
      : window.windowMinutes < 1440 ? `${window.windowMinutes / 60}ч`
      : `${window.windowMinutes / 1440}д`,
    title: window.title,
    used: clamp(window),
    resetsAt: window.resetsAt,
  }));
}

/** Keep the main Codex bucket's primary window stable in the header. */
export function primaryLimit(usage: Extract<CodexUsage, { ok: true }>): ShownLimit | null {
  const limits = shownLimits(usage);
  const primary = usage.windows.find((window) => window.primary);
  return limits.find((limit) => limit.key === primary?.key) ?? mostUsed(limits);
}

/** The fullest cap in a set, or null for an empty one. */
export function mostUsed(limits: ShownLimit[]): ShownLimit | null {
  let worst: ShownLimit | null = null;
  for (const limit of limits) if (!worst || limit.used > worst.used) worst = limit;
  return worst;
}

/** "через 1 ч 58 мин", or null once the reset is in the past — a countdown that has
 *  run out says nothing useful, and the next poll will bring the new window. */
export function untilReset(resetsAt: number | null, now: number): string | null {
  if (resetsAt === null) return null;
  const sec = Math.round((resetsAt - now) / 1000);
  if (sec <= 0) return null;
  if (sec < 3600) return `через ${Math.max(1, Math.round(sec / 60))} мин`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h < 24) return m > 0 ? `через ${h} ч ${m} мин` : `через ${h} ч`;
  return `через ${Math.round(h / 24)} дн`;
}
